/**
 * Tem thùng số nhảy: chia số lượng lô vào các thùng và nội dung mã QR in trên tem.
 * Dùng chung cho API (tạo tem khi nhập kho thành phẩm) và web (xem trước trên hộp thoại đóng gói).
 */

export interface CartonPlan {
  /** Số thùng thực tế (sau khi bỏ thùng rỗng) */
  totalBoxes: number;
  /** Số cái mỗi thùng, theo thứ tự thùng 1..N */
  quantities: number[];
  /** Thùng cuối khác quy cách (thùng lẻ, hoặc thùng cuối đã dồn phần lẻ) */
  lastBoxOdd: boolean;
}

/**
 * Số thùng của lô theo quy cách: mặc định thùng lẻ là một thùng riêng; mergeOddIntoLastBox = dồn phần lẻ vào thùng cuối
 * (chỉ khi có ít nhất 1 thùng chẵn).
 */
export function countCartons(totalQty: number, pcsPerBox: number, mergeOddIntoLastBox = false): number {
  const qty = Math.max(0, Math.floor(Number(totalQty) || 0));
  const pcs = Math.floor(Number(pcsPerBox) || 0);
  if (qty <= 0 || pcs <= 0) return 0;
  const full = Math.floor(qty / pcs);
  const odd = qty % pcs;
  if (odd === 0) return full;
  return mergeOddIntoLastBox && full >= 1 ? full : full + 1;
}

/**
 * Chia số lượng vào thùng: thùng 1..N-1 đủ quy cách, thùng cuối nhận phần còn lại.
 * totalBoxes bỏ trống thì tự tính (thùng lẻ riêng); thùng rỗng ở cuối bị bỏ, tổng các thùng luôn bằng số lượng lô.
 */
export function planCartons(totalQty: number, pcsPerBox: number, totalBoxes?: number | null): CartonPlan {
  const qty = Math.max(0, Math.floor(Number(totalQty) || 0));
  const pcs = Math.floor(Number(pcsPerBox) || 0);
  if (qty <= 0 || pcs <= 0) return { totalBoxes: 0, quantities: [], lastBoxOdd: false };

  const auto = countCartons(qty, pcs);
  const minBoxes = Math.max(1, Math.floor(qty / pcs));
  // Số thùng đã lưu nằm ngoài khoảng hợp lệ (ít hơn số thùng chẵn / nhiều hơn số thùng cần) thì tính lại
  const requested = Math.floor(Number(totalBoxes) || 0);
  const boxes = requested >= minBoxes && requested <= auto ? requested : auto;

  const quantities: number[] = [];
  for (let i = 1; i <= boxes; i += 1) {
    const q = i === boxes ? qty - pcs * (boxes - 1) : Math.min(pcs, qty - pcs * (i - 1));
    if (q <= 0) break;
    quantities.push(q);
  }
  const last = quantities[quantities.length - 1] ?? 0;
  return { totalBoxes: quantities.length, quantities, lastBoxOdd: quantities.length > 0 && last !== pcs };
}

export interface CartonQrInput {
  orderCode?: string | null;
  deliveryAddress?: string | null;
  product?: string | null;
  shift?: string | null;
  packer?: string | null;
  /** dd/mm/yyyy */
  packingDate?: string | null;
  boxQty?: number | null;
  /** Mã tem duy nhất (LBL-…) — quét khi giao hàng */
  labelCode: string;
}

/**
 * Nội dung QR tem thùng: [Số ĐH]|[ĐCGH]|[Sản phẩm]|[Ca]|[Người ĐG]|[Ngày ĐG]|[SL thùng], nối thêm |[Mã tem]
 * để mỗi thùng có QR riêng và quét giao hàng nhận đúng thùng.
 */
export function buildCartonQrText(input: CartonQrInput): string {
  const clean = (v: unknown) => String(v ?? '').replace(/[|\r\n]+/g, ' ').trim();
  return [
    clean(input.orderCode),
    clean(input.deliveryAddress),
    clean(input.product),
    clean(input.shift),
    clean(input.packer),
    clean(input.packingDate),
    clean(input.boxQty ?? ''),
    clean(input.labelCode),
  ].join('|');
}

/** Lấy mã tem từ nội dung quét: QR tem số nhảy (có dấu |) hoặc mã tem trần. */
export function extractCartonLabelCode(scanned: string): string {
  const raw = String(scanned ?? '').trim();
  if (!raw.includes('|')) return raw;
  const parts = raw.split('|').map((p) => p.trim()).filter(Boolean);
  return parts.find((p) => /^LBL-/i.test(p)) ?? parts[parts.length - 1] ?? raw;
}
